import { useCallback, useEffect, useMemo, useState } from "react";
import { batch, useDispatch } from "react-redux";
import { useHistory, useParams } from 'react-router';

import {
	EBroadcastTypes,
	ITranslateLangs,
	LanguagesAbbr,
	StaticVideo,
	SecondarySessionVideo,
	Session,
	SessionTypesEnum
} from "../../../../../../types/working-model";
import { copyStringToClipboard } from "../../../../../../utils/utils";
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import images from '../../../../../../images';
import {
	deleteSecondarySessionVideo,
	loadWorkingSession,
	setCurrentLanguage,
	setOnDemandVideo,
	setSessionLoading,
	updateSecondarySessionVideo
} from "../../../../../../store/actions/admin/create-event/session";
import { useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import TranslateLangs from '../../../../../../utils/translate-langs.json';
import Tags from "./tags";
import { GetSessionRtmp, GetSingleSession } from "../../../../../../connection/sessions";
import WaitingIndicator from "../../../../../general-ui/waiting-indicator/waiting-indicator";
import { availableLanguages, LanguageOption } from '../../editor/agenda-editor/select-languages';
import SelectInput, { SelectOption } from '../../../../../general-ui/select/select';
import useTranslationsV2 from "../../../shared/use-translations-v2";
import OpenGRButton from "../../../../../general-ui/open-greenroom-btn/open-greenroom-btn";
import { showAlert } from "../../../../../general-ui/alert/alert-service";
import { setGreenroomLoginError } from "../../../../../../store/actions/authentication";
import { useGetRecordingUrl } from "hooks/recording-jwt.hooks";
import { OptionalComponent } from "utils/optional-component";
import { useCanEnableMeetFiresides } from "hooks/channel.hooks";
import { clearPlaceholderVideo, setSessionPlaceholderVideo } from "store/actions/admin/create-event";
import { switchingLanguageAlert } from "@admin/create/translations/editor-language-selector";

import './session-settings.scss';
import NewStaticVideoSelector from "../../editor/videos/new-static-video-selector";
import { getVideo } from "connection/videos";
import VideoThumbnail from "@general-ui/video-thumbnail/video-thumbnail";
import BroadcastStreamingModal from "../../videos/broadcast-streaming-modal";
import { useSessionContext } from "contexts/session-context";

const DurationLabel = ({ duration, recording_duration_ms }: { duration?: number, recording_duration_ms?: number }) => {
	if (duration) {
		return <time className="duration">{new Date(duration * 1000).toISOString().substr(11, 8)}</time>;
	} else if (recording_duration_ms) {
		return <time className="duration">{new Date(recording_duration_ms).toISOString().substr(11, 8)}</time>;
	} else {
		return null;
	}
};

function PanelBlockStreaming(): JSX.Element {
	const user = useTypedSelector(state => state.AuthReducer.user);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const greenroomLoginError = useTypedSelector(state => state.AuthReducer.greenroomLoginError);
	const secondaryVideo = useTypedSelector(state => state.CreateSessionReducer.secondarySessionVideo);
	const currentSessionLang = useTypedSelector(state => state.CreateSessionReducer.workingSession?.current_language);

	const [onDemandVideoModalOpen, setOnDemandVideoModalOpen] = useState(false);
	const [editStreamModalOpen, setEditStreamModalOpen] = useState(false);

	const [mungedSecondaryVideo, setMungedSecondaryVideo] = useState<StaticVideo | undefined>(undefined);
	const [secondaryVideoModalOpen, setSecondaryVideoModalOpen] = useState(false);
	const [viewRtmps, setViewRtmps] = useState(false);
	const [rtmpsAddress, setRtmpsAddress] = useState('');
	const [streamKey, setStreamKey] = useState('');
	const [languageUsedForRTMP, setLanguageUsedForRTMP] = useState<LanguagesAbbr | null>(null);
	const [loadingRtmps, setLoadingRtmps] = useState(false);
	const [copiedText, setCopiedText] = useState('');
	const { language }: { language: LanguagesAbbr } = useParams();
	const useV2 = useTranslationsV2();
	const { onDemandVideo: staticVideo, failedToLoadVideo } = useSessionContext();
	const getRecordingUrl = useGetRecordingUrl();
	const canEnableMeet = useCanEnableMeetFiresides();

	const meetFireside = workingSession?.fireside_session_settings?.settings?.use_google_meet;
	const [languages] = useState<{ [x: string]: string }>(TranslateLangs.reduce((pre: ITranslateLangs, cur) => {
		pre[cur.code] = cur.label;
		return pre;
	}, {}));

	useEffect(() => {
		if (copiedText) setTimeout(() => setCopiedText(""), 2000);
	}, [copiedText]);

	const dispatch = useDispatch();
	const params = useParams<Record<string, string>>();
	const history = useHistory();
	const editVideo = () => {
		if (workingSession?.session_type === SessionTypesEnum.onDemand) {
			setOnDemandVideoModalOpen(true);
		} else {
			setEditStreamModalOpen(true);
		}
	};

	const languageOptions: LanguageOption[] = useMemo(() => {
		return availableLanguages([workingSession as Session], SessionTypesEnum.onDemand);
	}, [workingSession]);

	// We should always have a default language set on the session, but if for some reason we don't, set the first
	const [currentInputLang, setCurrentInputLang] = useState<string>(languageOptions?.[0]?.label ?? workingSession?.languages[0]);

	useEffect(() => {
		if (!user || !token) return;
		const fetchSecondaryVideo = async () => {
			if (secondaryVideo?.static_video_uuid) {
				try {
					const video = await getVideo(token, user.active_channel, secondaryVideo.static_video_uuid);
					setMungedSecondaryVideo(video);
				} catch (error) {
					showAlert({
						message: "Error",
						description: "Error fetching placeholder video"
					});
				}
			} else {
				setMungedSecondaryVideo(undefined);
			}
		};

		fetchSecondaryVideo();
	}, [
		secondaryVideo,
		user,
		dispatch,
		token,
	]);

	useEffect(() => {
		if (greenroomLoginError && workingSession?.broadcast_type === EBroadcastTypes.greenroom) {
			showAlert({
				message: "Login Failed",
				description: greenroomLoginError,
				type: "error",
				afterClose: () => {
					dispatch(setGreenroomLoginError(false));
				}
			});
		}
	}, [dispatch, greenroomLoginError, workingSession?.broadcast_type]);

	const revealRtmps = useCallback(async () => {
		if (workingSession && token) {
			setViewRtmps(true);
			setLoadingRtmps(true);
			try {
				const {
					rtmps_address,
					stream_key
				} = await GetSessionRtmp(
					token,
					workingSession.uuid,
					workingSession.streaming_options.single_stream ? workingSession.default_language : language
				);

				setLanguageUsedForRTMP(workingSession.streaming_options.single_stream ? workingSession.default_language as LanguagesAbbr : language);
				setRtmpsAddress(rtmps_address);
				setStreamKey(stream_key);
			} catch (e) {
				console.error(e);
				setViewRtmps(false);
				showAlert({
					message: 'Unable to display RTMP info',
					description: 'We ran into an issue while trying to display the RTMP info. Please try again later.',
					type: 'error',
					duration: 5000,
				});
			} finally {
				setLoadingRtmps(false);
			}
		}
	}, [language, token, workingSession]);


	const copy = useCallback((str: string) => {
		return () => {
			setCopiedText(str);
			copyStringToClipboard(str);
		};
	}, []);

	const getCopyButton = useCallback((text: string) => (
		<button className="no-style no-padding read-only-exception" onClick={copy(text)}>
			{copiedText === text
				? <>Copied!</>
				: <><Icon name={ICONS.COPY_FILLED} size={16} color={COLORS.WHITE} /></>
			}
		</button>
	), [copiedText, copy]);

	const handleLangInputChange = useCallback(async (selectedCode: string) => {
		const filteredSelectedLang = languageOptions.filter(option => option.code === selectedCode)[0];
		setCurrentInputLang(filteredSelectedLang.label);
		if (params.id && params.session && workingSession && token) {
			if (!useV2) {
				dispatch(setSessionLoading(true));
				switchingLanguageAlert();

				const _session = await GetSingleSession(token, workingSession.session);

				if (_session) {
					dispatch(loadWorkingSession(_session));
				}
			}

			history.replace(`/admin/events/create/${params.id}/session/${params.session}/${filteredSelectedLang.code}/settings`);
			batch(() => {
				dispatch(setCurrentLanguage(filteredSelectedLang.code));
				dispatch(setSessionLoading(false));
			});
		}

	}, [dispatch, history, languageOptions, params.id, params.session, token, workingSession, useV2]);

	const renderLangSelectDropdown = useCallback(() => {
		if (workingSession &&
			workingSession.session_type === SessionTypesEnum.onDemand &&
			!workingSession.streaming_options?.single_stream &&
			workingSession.languages.length > 1) {
			return (
				<div className='session-language-select'>
					<label className='session-language-label'>Session language</label>
					<SelectInput
						options={languageOptions as SelectOption[]}
						selected={params.language}
						onChange={handleLangInputChange}
						small
						placeholder={languageOptions.filter(lang => lang.code === params.language)?.[0].label}
						keyType={'code'}
						mainIcon={
							<Icon
								name={ICONS.KEYBOARD_ARROW_DOWN}
								size={14}
								color="white"
							/>
						}
					/>
				</div>
			);
		} else return;
	}, [handleLangInputChange, languageOptions, params.language, workingSession]);

	const renderAddedVideoTag = useCallback((video: StaticVideo) => {
		switch (video.type) {
			case "recorded":
			case "uploaded": {
				return (
					<>
						<div className="settings-type" style={{ marginTop: 16 }}>
							<div className="icon-gradient">
								<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
							</div>

							<label>Added video</label>
						</div>
						{renderLangSelectDropdown()}
						<div className="video-display">
							{video.thumbnail_url ? (
								<VideoThumbnail src={getRecordingUrl(video.thumbnail_url)} alt={video.name} />
							) : (
								<div className="video-placeholder" />
							)}
							<div className="video-data">
								<label>{video.name}</label>
								<DurationLabel
									// duration={'duration' in video && video.duration ? video.duration : undefined}
									recording_duration_ms={video.duration_ms}
								/>
							</div>
						</div>
					</>
				);
			}
			case "url": {
				return (
					<>
						<div className="settings-type" style={{ marginTop: 16 }}>
							<div className="icon-gradient">
								<Icon name={ICONS.PAPERCLIP} size={12} color={COLORS.BLACK} />
							</div>

							<label>Linked video</label>
						</div>

						<div className="settings-section">
							<h6>Link</h6>
							<div className="video-link">
								<p>{video.source}</p>

								<button onClick={copy(video.source ?? '')}><Icon name={ICONS.COPY} size={12} color={COLORS.LIGHT_GRAY} /></button>
							</div>
							{renderLangSelectDropdown()}
						</div>
					</>
				);
			}
			case "embed": {
				return (
					<>
						<div className="settings-type" style={{ marginTop: 16 }}>
							<div className="icon-gradient">
								<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
							</div>

							<label>Embedded iframe</label>
						</div>
						<div className="settings-section">
							<h6>Code</h6>
							<div className="video-link">
								<p>{video.source}</p>

								<button onClick={copy(video.source ?? '')}><Icon name={ICONS.COPY} size={12} color={COLORS.LIGHT_GRAY} /></button>
							</div>
							{renderLangSelectDropdown()}
						</div>
					</>
				);
			}
			default: {
				return (
					<>
						<div className="settings-type" style={{ marginTop: 16 }}>
							<div className="icon-gradient">
								<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
							</div>

							<label>Add video</label>
							{renderLangSelectDropdown()}
						</div>
					</>
				);
			}
		}
	}, [renderLangSelectDropdown, getRecordingUrl, copy]);

	const renderVideoType = useMemo(() => {
		if (workingSession?.session_type === SessionTypesEnum.onDemand) {
			//on demand sessions can have several types or not be defined yet

			if (staticVideo) {
				return renderAddedVideoTag(staticVideo);
			} else {
				return (
					<>
						<OptionalComponent display={failedToLoadVideo}>
							<div className="missing-video">Could not load video</div>
						</OptionalComponent>
						<div className="settings-type" style={{ marginTop: 16 }}>
							<div className="icon-gradient">
								<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
							</div>

							<label>Add video</label>
						</div>
						{workingSession.session_type === SessionTypesEnum.onDemand &&
							!workingSession.streaming_options?.single_stream &&
							workingSession.languages.length > 1 &&
							<div className='session-language-select'>
								<label className='session-language-label'>Session language</label>

								<SelectInput
									options={languageOptions as SelectOption[]}
									selected={currentInputLang}
									onChange={handleLangInputChange}
									small
									placeholder={currentInputLang}
									mainIcon={
										<Icon
											name={ICONS.KEYBOARD_ARROW_DOWN}
											size={14}
											color="white"
										/>
									}
								/>
							</div>
						}
					</>
				);
			}
		} else {
			switch (workingSession?.broadcast_type) {
				case EBroadcastTypes.greenroom: {
					return (
						<>
							<div className="settings-type" style={{ marginTop: 16 }}>
								<div className="icon-greenroom">
									<img src={images.GreenroomLogo} alt="greenroom" />
								</div>

								<label>Greenroom Streaming</label>
							</div>
						</>
					);
				}
				case EBroadcastTypes.fireside: {
					return (
						<>
							<div className="settings-type" style={{ marginTop: 16 }}>
								<div className="icon-gradient">
									<Icon name={ICONS.CAMERA_ON} size={12} color={COLORS.BLACK} />
								</div>

								<label>{meetFireside ? "Google Meet" : "Native Fireside Video"}</label>
							</div>
						</>
					);
				}
				case EBroadcastTypes.rtmps: {
					return (
						<>
							<div className="settings-type" style={{ marginTop: 16 }}>
								<div className="icon-gradient">
									<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
								</div>

								<label>RTMP streaming</label>
							</div>
						</>
					);
				}

				case "embed": {

					return (
						<>
							<div className="settings-type" style={{ marginTop: 16 }}>
								<div className="icon-gradient">
									<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
								</div>

								<label>Embedded iframe</label>
							</div>
							<div className="settings-section">
								<h6>Code</h6>
								<div className="video-link">
									<p>{staticVideo?.source}</p>

									<button onClick={copy(staticVideo?.source ?? '')}><Icon name={ICONS.COPY} size={12} color={COLORS.LIGHT_GRAY} /></button>
								</div>
							</div>
						</>
					);
				}
			}

			return (
				<>
					<div className="settings-type" style={{ marginTop: 16 }}>
						<div className="icon-gradient">
							<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
						</div>

						<label>Events streaming</label>
					</div>
				</>
			);
		}
	}, [workingSession, renderAddedVideoTag, languageOptions, handleLangInputChange, currentInputLang, copy, currentSessionLang, meetFireside, staticVideo]);

	const handleRemoveSecondaryVideo = () => {
		if (!workingSession || !workingEvent || !token) return;
		batch(() => {
			dispatch(deleteSecondarySessionVideo(workingSession.session, token));
			dispatch(clearPlaceholderVideo(workingSession.uuid, language));
		});
	};

	const openSecondaryVideoModal = () => {
		//set video modal open with the secondary video boolean set to true
		setSecondaryVideoModalOpen(true);
	};

	const handleSecondaryVideoSelect = (videos: StaticVideo[] | null) => {
		if (!token || !workingSession) return;


		const video = videos?.[0];

		if (video) {
			const source = video.playback_url || video.source;
			const secondaryVideo: SecondarySessionVideo = {
				playback_url: video.playback_url || video.source,
				static_video_uuid: video.uuid,
				session: workingSession.session,
			};

			batch(() => {
				dispatch(updateSecondarySessionVideo(secondaryVideo, token));
				dispatch(setSessionPlaceholderVideo({
					session_uuid: workingSession.uuid,
					playback_url: source,
					language: 'all',
					type: 'placeholder_video'
				}));
			});
		}

		closeVideoModal();
	};

	const handleSelectOnDemandVideo = (videos: StaticVideo[] | null) => {
		if (!token || !workingSession) return;

		if (
			workingSession?.session_type === SessionTypesEnum.onDemand
			|| (workingSession?.session_type === SessionTypesEnum.broadcast && workingSession.broadcast_type === EBroadcastTypes.embed)
		) {

			const lang = workingSession.streaming_options.single_stream ? workingSession.default_language : language;

			// Should only be one video.
			if (videos && videos.length > 0) {
				const video = videos[0];
				dispatch(setOnDemandVideo(video, lang));
			} else {
				dispatch(setOnDemandVideo(null, lang));
			}
		}

		closeVideoModal();
	};


	const closeVideoModal = useCallback(() => {
		setOnDemandVideoModalOpen(false);
		setSecondaryVideoModalOpen(false);
	}, []);

	const showSecondarySection = useMemo(() => {
		const allowedType = [SessionTypesEnum.broadcast, SessionTypesEnum.fireside];
		return workingSession?.session_type && allowedType.includes(workingSession.session_type);
	}, [workingSession?.session_type]);

	useEffect(() => {
		if (viewRtmps && languageUsedForRTMP !== language && !workingSession?.streaming_options.single_stream) revealRtmps();
	}, [language, languageUsedForRTMP, revealRtmps, viewRtmps, workingSession]);

	if (!workingSession) return <></>;

	return (
		<>
			<div className="panel-block settings-block">
				<div className="settings-block-header">
					<h5>Streaming</h5>
					<OptionalComponent display={workingSession.session_type !== SessionTypesEnum.fireside || canEnableMeet}>
						<div className="panel-block-edit-container">
							<button onClick={editVideo}>
								<Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} />
							</button>
						</div>
					</OptionalComponent>
				</div>
				{renderVideoType}
				{workingSession?.session_type === SessionTypesEnum.broadcast &&
					<>
						{workingSession.broadcast_type === EBroadcastTypes.rtmps ?
							(
								viewRtmps ? (
									<>
										{loadingRtmps ? (
											<WaitingIndicator />
										) : (
											<>
												<div className="rtmp-data">
													{workingSession.streaming_options.single_stream ? (
														<>
															<div className="rtmp-label">{`Session language${workingSession.languages.length > 1 ? 's' : ''}`}</div>
															<Tags heading="" data={workingSession.languages.map(v => languages[v])} threshold={3} />
														</>
													) : (
														<>
															<div className="rtmp-label">{`Session language`}</div>
															<Tags heading="" data={[languages[language]]} threshold={1} />
														</>
													)}
													<div className={'rtmp-label'}>RTMP address</div>
													<div className="rtmp-value-row">
														<div className="rtmp-value">{rtmpsAddress}</div>
														<div>{getCopyButton(rtmpsAddress)}</div>
													</div>
												</div>
												<div className="rtmp-data">
													<div className={'rtmp-label'}>RTMP stream key</div>
													<div className="rtmp-value-row">
														<div className="rtmp-value">{streamKey}</div>
														<div>{getCopyButton(streamKey)}</div>
													</div>
												</div>
											</>
										)}
									</>
								) : (
									<div className="show-rtmp-button">
										<button onClick={revealRtmps} className="read-only-exception">Show RTMP info</button>
									</div>
								)
							) : (<></>)}
					</>
				}
				{showSecondarySection && <>
					{mungedSecondaryVideo && renderAddedVideoTag(mungedSecondaryVideo)}

					<div className="secondary-video-buttons">
						<OptionalComponent display={workingSession.session_type === SessionTypesEnum.broadcast}>
							<div className="open-gr-btn-wrapper">
								<OpenGRButton session={workingSession} />
							</div>
						</OptionalComponent>

						<button
							className="small"
							onClick={openSecondaryVideoModal}
						>
							{secondaryVideo ? "Edit placeholder video" : "Add a placeholder video"}
						</button>

						<OptionalComponent display={!!mungedSecondaryVideo}>
							<button disabled={!secondaryVideo} className="small" onClick={handleRemoveSecondaryVideo}>Remove placeholder video</button>
						</OptionalComponent>
					</div>
				</>
				}
			</div>

			<OptionalComponent display={editStreamModalOpen}>
				<BroadcastStreamingModal
					onClose={() => setEditStreamModalOpen(false)}
				/>
			</OptionalComponent>

			<OptionalComponent display={onDemandVideoModalOpen}>
				<NewStaticVideoSelector
					onSelect={handleSelectOnDemandVideo}
					selectedVideos={staticVideo ? [staticVideo] : []}
					handleCancel={closeVideoModal}
				/>
			</OptionalComponent>

			<OptionalComponent display={secondaryVideoModalOpen}>
				<NewStaticVideoSelector
					onSelect={handleSecondaryVideoSelect}
					selectedVideos={mungedSecondaryVideo ? [mungedSecondaryVideo] : []}
					handleCancel={closeVideoModal}
				/>
			</OptionalComponent>

		</>
	);
}


export default PanelBlockStreaming;

